import React, { useState, useEffect } from 'react';
import { TextField, Box, ThemeProvider } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import { orangeTheme } from './theme';

const DateTimeRangePicker = ({
  formData,
  setFormData,
  IsDisabled,
  isModifyClassName = null,
  errors = {},
}) => {

  const {
    scheduleStartTime,
    scheduleEndTime,
    scheduleDate,
    scheduleEndDate
  } = formData;

  const {
    scheduleDateError,
    scheduleStartTimeError,
    scheduleEndDateError,
    scheduleEndTimeError
  } = errors;

  const [startDateTime, setStartDateTime] = useState(null);
  const [endDateTime, setEndDateTime] = useState(null);

  // NEW: validation errors (added but does not affect design)
  const [rangeErrors, setRangeErrors] = useState({
    start: "",
    end: ""
  });

  // Set initial Start
  useEffect(() => {
    if (scheduleDate && scheduleStartTime) {
      const date = dayjs(`${scheduleDate.split("T")[0]}T${scheduleStartTime}`);
      setStartDateTime(date);
    }
  }, [scheduleDate, scheduleStartTime]);

  // Set initial End
  useEffect(() => {
    if (scheduleEndDate && scheduleEndTime) {
      const date = dayjs(`${scheduleEndDate.split("T")[0]}T${scheduleEndTime}`);
      setEndDateTime(date);
    }
  }, [scheduleEndDate, scheduleEndTime]);

  // NEW: Validation only
  useEffect(() => {
    let startErr = "";
    let endErr = "";

    if (startDateTime && endDateTime) {
      if (endDateTime.isBefore(startDateTime)) {
        endErr = "End must be after start";
      }
      if (startDateTime.isAfter(endDateTime)) {
        startErr = "Start must be before end";
      }
    }

    setRangeErrors({
      start: startErr,
      end: endErr
    });
  }, [startDateTime, endDateTime]);

  // Update formData
  useEffect(() => {
    if (startDateTime) {
      setFormData(prev => ({
        ...prev,
        scheduleDate: startDateTime.format('YYYY-MM-DDTHH:mm:ss'),
        scheduleStartTime: startDateTime.format('HH:mm'),
      }));
    }

    if (endDateTime) {
      setFormData(prev => ({
        ...prev,
        scheduleEndDate: endDateTime.format('YYYY-MM-DDTHH:mm:ss'),
        scheduleEndTime: endDateTime.format('HH:mm'),
      }));
    }
  }, [startDateTime, endDateTime]);

  const [touched, setTouched] = useState(false);

  return (
    <ThemeProvider theme={orangeTheme}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>

        {isModifyClassName ? (
          <div className="row">

            {/* START DATE-TIME */}
            <div className={`mb-3 col-12 col-md-6 col-sm-12`}>
              <label className="form-label">Schedule From</label>

              <DateTimePicker
                value={startDateTime}
                onOpen={() => setTouched(true)}
                onChange={(newValue) => setStartDateTime(newValue)}
                disabled={IsDisabled}
                ampm={false}
                // minDateTime={dayjs()}
                // minDateTime={startDateTime}
                              disablePast={false}
                              minDateTime={null}

                format="DD/MM/YYYY HH:mm"
                slotProps={{
                  textField: {
                    variant: "outlined",
                    placeholder: "Select date and time",
                    InputLabelProps: { shrink: true },
                    fullWidth: true,
                    label: '',
                    className: 'form-control',
                    error: touched && !formData.scheduleDateTime,
                  }
                }}
              />

              {/* NEW: validation message added but UI unchanged */}
              {(scheduleDateError || scheduleStartTimeError || rangeErrors.start) && (
                <span className="errormsg" style={{ fontWeight: 'bold', color: 'red' }}>
                  {rangeErrors.start || scheduleDateError || scheduleStartTimeError}
                </span>
              )}
            </div>

            {/* END DATE-TIME */}
            <div className={`mb-3 col-12 col-md-6 col-sm-12`}>
              <label className="form-label">Schedule To</label>

              <DateTimePicker
                value={endDateTime}
                onChange={(newValue) => setEndDateTime(newValue)}
                disabled={IsDisabled || !startDateTime}
                ampm={false}
                minDateTime={startDateTime}
                format="DD/MM/YYYY HH:mm"
                slotProps={{
                  textField: {
                    variant: 'outlined',
                    placeholder: "Select date and time",
                    fullWidth: true,
                    InputLabelProps: { shrink: true },
                    label: '',
                    className: 'form-control',
                  },
                }}
              />

              {/* NEW: validation message added but UI unchanged */}
              {(scheduleEndDateError || scheduleEndTimeError || rangeErrors.end) && (
                <span className="errormsg" style={{ fontWeight: 'bold', color: 'red' }}>
                  {rangeErrors.end || scheduleEndDateError || scheduleEndTimeError}
                </span>
              )}
            </div>
          </div>
        ) : (
          <>
            {/* EXACT SAME VALIDATION LOGIC for the second layout */}
            <div className={`mb-3 col-12 col-md-4 col-sm-12`}>
              <label className="form-label">Schedule From</label>

              <DateTimePicker
                value={startDateTime}
                onOpen={() => setTouched(true)}
                onChange={(newValue) => setStartDateTime(newValue)}
                disabled={IsDisabled}
                ampm={false}
                minDateTime={dayjs()}
                format="DD/MM/YYYY HH:mm"
                slotProps={{
                  textField: {
                    variant: "outlined",
                    placeholder: "Select date and time",
                    InputLabelProps: { shrink: true },
                    fullWidth: true,
                    label: '',
                    className: 'form-control',
                    error: touched && !formData.scheduleDateTime
                  }
                }}
              />

              {(scheduleDateError || scheduleStartTimeError || rangeErrors.start) && (
                <span className="errormsg" style={{ fontWeight: 'bold', color: 'red' }}>
                  {rangeErrors.start || scheduleDateError || scheduleStartTimeError}
                </span>
              )}
            </div>

            <div className={`mb-3 col-12 col-md-4 col-sm-12`}>
              <label className="form-label">Schedule To</label>

              <DateTimePicker
                value={endDateTime}
                onChange={(newValue) => setEndDateTime(newValue)}
                disabled={IsDisabled || !startDateTime}
                ampm={false}
                minDateTime={startDateTime}
                format="DD/MM/YYYY HH:mm"
                slotProps={{
                  textField: {
                    variant: 'outlined',
                    placeholder: "Select date and time",
                    fullWidth: true,
                    InputLabelProps: { shrink: true },
                    label: '',
                    className: 'form-control',
                  },
                }}
              />

              {(scheduleEndDateError || scheduleEndTimeError || rangeErrors.end) && (
                <span className="errormsg" style={{ fontWeight: 'bold', color: 'red' }}>
                  {rangeErrors.end || scheduleEndDateError || scheduleEndTimeError}
                </span>
              )}
            </div>
          </>
        )}
      </LocalizationProvider>
    </ThemeProvider>
  );
};

export default DateTimeRangePicker;
